import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES } from "@/data/services";
import { CAD_SERVICES, CAD_SERVICE_CATEGORIES } from "@/data/cadServices";
import ContourBackground from "@/components/ContourBackground";
import GridFillerCards from "@/components/GridFillerCards";
import { padForGrid } from "@/lib/gridUtils";
import useSeo from "@/hooks/useSeo";

export default function Services() {
  useSeo({
    title: "Services — Tri-Axis XYZ CAD Solutions",
    description:
      "23 CAD drafting services across Land-Survey CAD (ALTA, boundary, topo, plats, ROW, GIS) and CAD Drafting Beyond Survey (architectural, structural, MEP/BIM, mechanical, civil, 3D rendering, PDF-to-CAD, solar PV, signage).",
  });

  return (
    <div data-testid="page-services">
      <Hero />
      <SurveyGroup />
      <CadGroup />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-slate-200 bg-paper-dark/40">
      <ContourBackground variant="light" opacity={0.5} seed={5} />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
            Services Index
          </div>
          <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900">
            23 CAD services.{" "}
            <span className="text-emerald-700">One production partner.</span>
          </h1>
          <p className="mt-5 max-w-3xl text-base sm:text-lg text-slate-600 leading-relaxed">
            We cover <strong className="text-slate-900">Land Survey CAD</strong> (11
            disciplines led by licensed surveyors) and{" "}
            <strong className="text-slate-900">CAD Drafting Beyond Survey</strong> (12
            disciplines across architecture, structural, MEP, mechanical, electrical,
            civil and specialty work). Pick a discipline below — every page includes
            deliverables, sub-services and a quote form.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="#survey-services"
              data-testid="services-jump-survey"
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2.5 bg-white"
            >
              🧭 Land Survey CAD (11) <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#cad-services"
              data-testid="services-jump-cad"
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2.5 bg-white"
            >
              📐 CAD Beyond Survey (12) <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Right-side keyword panel — fills the blank area with company-positioning content. */}
        <aside className="lg:col-span-5">
          <div className="border border-slate-200 bg-white p-6 lg:p-7 corner-marks">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              Why outsource to Tri-Axis XYZ
            </div>
            <ul className="mt-4 space-y-3">
              {[
                { k: "Survey-licensed QC", v: "Every drawing reviewed by a licensed surveyor or discipline lead." },
                { k: "Your CAD standards", v: "Templates, blocks, layers and plot styles adopted on day one." },
                { k: "24–72 hr turnaround", v: "Overnight production aligned to USA, Canada and Australia." },
                { k: "NDA-protected", v: "Encrypted file transfer, role-based access, 60-day retention." },
                { k: "Transparent pricing", v: "Hourly, per-project, or dedicated-FTE. No hidden seat licenses." },
              ].map((item) => (
                <li key={item.k} className="flex items-start gap-3">
                  <span className="mt-1 inline-block h-2 w-2 bg-orange-survey shrink-0" />
                  <div>
                    <div className="display text-sm font-extrabold tracking-tight text-slate-900">
                      {item.k}
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed">{item.v}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="display text-2xl font-extrabold tracking-tighter text-topo">23</div>
                <div className="mono text-[9px] tracking-[0.22em] uppercase text-slate-500 mt-0.5">Service lines</div>
              </div>
              <div>
                <div className="display text-2xl font-extrabold tracking-tighter text-topo">3</div>
                <div className="mono text-[9px] tracking-[0.22em] uppercase text-slate-500 mt-0.5">Continents</div>
              </div>
              <div>
                <div className="display text-2xl font-extrabold tracking-tighter text-topo">5K+</div>
                <div className="mono text-[9px] tracking-[0.22em] uppercase text-slate-500 mt-0.5">Drawings shipped</div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function SurveyGroup() {
  return (
    <section
      id="survey-services"
      data-testid="services-survey-group"
      className="border-b border-slate-200 scroll-mt-20"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
          <div className="lg:col-span-8">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              Part 01 · 🧭 Land Survey CAD
            </div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
              Eleven survey-CAD services led by{" "}
              <span className="text-emerald-700">licensed surveyors</span>.
            </h2>
            <p className="mt-3 text-slate-600">
              ALTA, boundary, topo, subdivision plats, construction staking, as-built,
              floodplain certs, ROW, GIS, site plans and 3D modeling.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/survey-techies"
              data-testid="services-survey-techies-link"
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2.5 bg-white"
            >
              Hire a Survey Techie <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {SERVICE_CATEGORIES.map((cat) => (
          <CategoryBlock
            key={cat.title}
            category={cat}
            allServices={SERVICES}
            basePath="/services"
            testidPrefix="services-card"
          />
        ))}
      </div>
    </section>
  );
}

function CadGroup() {
  return (
    <section
      id="cad-services"
      data-testid="services-cad-group"
      className="border-b border-slate-200 bg-paper-dark/20 scroll-mt-20"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
          <div className="lg:col-span-8">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              Part 02 · 📐 CAD Drafting Beyond Survey
            </div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
              Twelve non-survey CAD disciplines.{" "}
              <span className="text-orange-survey">One bench.</span>
            </h2>
            <p className="mt-3 text-slate-600">
              Architecture, structural, MEP/BIM, mechanical, electrical, plumbing,
              HVAC, civil-engineering, 3D rendering, PDF-to-CAD, solar PV and signage.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/cad-techies"
              data-testid="services-cad-techies-link"
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2.5 bg-white"
            >
              Hire a CAD Techie <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {CAD_SERVICE_CATEGORIES.map((cat) => (
          <CategoryBlock
            key={cat.title}
            category={cat}
            allServices={CAD_SERVICES}
            basePath="/cad-techies#cad"
            testidPrefix="cad-services-card-detail"
            fillerKind="cad"
          />
        ))}
      </div>
    </section>
  );
}

/* ---------- Reusable: a categorized row of service cards ---------- */
function CategoryBlock({ category, allServices, basePath, testidPrefix, fillerKind = "survey" }) {
  const items = category.slugs
    .map((slug) => allServices.find((s) => s.slug === slug))
    .filter(Boolean);
  const isHashed = basePath.includes("#");
  // Number of filler cards needed so the last row of the lg:grid-cols-3 grid is never visually empty.
  const fillerCount = padForGrid(items.length);
  return (
    <div className="mb-10 last:mb-0">
      <div className="flex items-baseline justify-between mb-4">
        <div className="display text-lg sm:text-xl font-extrabold tracking-tight text-slate-900">
          {category.title}
        </div>
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
          {items.length} services
        </div>
      </div>
      {category.blurb && (
        <div className="text-sm text-slate-600 mb-4 -mt-2">{category.blurb}</div>
      )}
      <div data-reveal="up" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        {items.map((s, i) => {
          const href = isHashed
            ? `${basePath}-${s.slug}`
            : `${basePath}/${s.slug}`;
          return (
            <Link
              key={s.slug}
              to={href}
              data-testid={`${testidPrefix}-${s.slug}`}
              className="tri-card group bg-white p-6 lg:p-7 flex flex-col gap-4 min-h-[220px]"
            >
              <div className="flex items-center justify-between">
                <span className="text-3xl leading-none">{s.emoji}</span>
                <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-400">
                  {String(i + 1).padStart(2, "0")} / {items.length}
                </span>
              </div>
              <div>
                <div className="display text-lg sm:text-xl font-extrabold tracking-tight text-slate-900">
                  {s.name}
                </div>
                <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {s.short}
                </div>
              </div>
              <div className="mt-auto flex items-center gap-1 text-sm font-semibold text-topo">
                {isHashed ? "View sub-services" : "View service"}{" "}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          );
        })}
        <GridFillerCards count={fillerCount} kind={fillerKind} startIndex={items.length} total={items.length + fillerCount} />
      </div>
    </div>
  );
}
